import { useUserStore } from "@/stores";
import { productFruits } from "product-fruits";
import { fetchProductFruitsHmac } from "@/services/api/productFruits/productFruitsApi";
import type {
  ProductFruitsUserData,
  ProductFruitsCachedData
} from "@/types/productFruits.types";

const CACHE_KEY = "pf_hmac_cache";

/**
 * Get cached HMAC data from sessionStorage
 */
const getCachedHmac = (username: string): ProductFruitsCachedData | null => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: ProductFruitsCachedData = JSON.parse(cached);

    // Validate cache - only check username match
    if (data.username !== username) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error reading cached HMAC:", error);
    return null;
  }
};

/**
 * Cache HMAC data in sessionStorage
 */
const cacheHmac = (data: ProductFruitsCachedData): void => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Error caching HMAC:", error);
  }
};

/**
 * Fetch HMAC from backend API
 */
const getProductFruitsHmac =
  async (): Promise<ProductFruitsCachedData | null> => {
    try {
      const response = await fetchProductFruitsHmac();
      const fetchedHmacData = response.data;

      if (
        fetchedHmacData.hmac &&
        fetchedHmacData.workspace_id &&
        fetchedHmacData.username
      ) {
        console.log("✓ Successfully fetched HMAC");

        return {
          hmac: fetchedHmacData.hmac,
          username: fetchedHmacData.username,
          workspace_id: fetchedHmacData.workspace_id
        };
      }

      console.error("✗ Response missing required fields");
      return null;
    } catch (error: any) {
      console.error("✗ Failed to fetch ProductFruits HMAC:", error);

      // Log more details for debugging
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
      }

      return null;
    }
  };

/**
 * Initializes the Product Fruits widget with user-specific data and HMAC authentication.
 * This function retrieves user information from the store and passes it to the Product Fruits widget.
 * Uses session caching to avoid fetching HMAC on every request.
 */
export const initProductFruits = async (language?: string): Promise<void> => {
  try {
    // Get language with fallback chain
    const lang = language || localStorage.getItem("locale") || "en";

    // Get the user store instance
    const user = useUserStore();

    // Extract user information from the store
    const userInfo = user.getUserInfo;

    // Check if user info is available
    if (!userInfo || !userInfo["email"]) {
      console.warn(
        "User info not available, skipping ProductFruits initialization"
      );
      return;
    }

    const username = userInfo["email"];

    // Get company name from user store
    const companyUserDetails = user.getCompanyUserDetailsInfo;
    const companyName = companyUserDetails?.company_info?.name || "";

    // Check for cached HMAC
    let hmacData = getCachedHmac(username);

    // If no valid cache, fetch new HMAC
    if (!hmacData) {
      console.log("Fetching new HMAC...");
      const fetchedHmac = await getProductFruitsHmac();

      if (fetchedHmac) {
        hmacData = fetchedHmac;
        cacheHmac(hmacData);
      }
    } else {
      console.log("Using cached HMAC");
    }

    // Check if we have workspace_id (required for initialization)
    if (!hmacData || !hmacData.workspace_id) {
      console.error("ProductFruits workspace ID not available");
      return;
    }

    // Prepare user data object to pass to Product Fruits
    // IMPORTANT: Use username from HMAC response to ensure it matches the HMAC generation
    const productFruitsUserData: ProductFruitsUserData = {
      username: hmacData.username, // Must match the username used to generate HMAC
      email: userInfo["email"],
      firstname: userInfo["custom:first_name"] || "",
      lastname: userInfo["custom:last_name"] || "",
      props: {
        Company: companyName
      }
    };

    // Add HMAC if available
    if (hmacData.hmac) {
      productFruitsUserData.hmac = {
        hash: hmacData.hmac
      };
      console.log("ProductFruits initialized with HMAC authentication");
    } else {
      console.warn(
        "ProductFruits HMAC not available, initializing without HMAC authentication"
      );
    }

    // Initialize the Product Fruits widget with workspace ID, language, and user data
    productFruits.init(hmacData.workspace_id, lang, productFruitsUserData);
  } catch (error) {
    console.error("Exception in initProductFruits:", error);
  }
};
